import { FileText } from 'lucide-react';

import type { EndsFdaWarningBucket, EndsFdaWarnings } from '@ballerine/common';

import { ctw } from '@/common';
import { ReportWrapper } from '../../../report-shared/components/ReportWrapper';
import { FdaInspectionHistory } from './FdaInspectionHistory';
import { FdaLettersTable } from './FdaLettersTable';
import { isAbsoluteHttpUrl } from './utils';

type FdaWarningsSectionProps = {
  fdaWarnings?: EndsFdaWarnings | null;
};

const FdaGlyph = () => (
  <span
    className="inline-flex h-6 w-8 items-center justify-center rounded border border-slate-300 text-[10px] font-bold text-slate-500"
    aria-hidden="true"
  >
    FDA
  </span>
);

const confidenceSuffix = (bucket: EndsFdaWarningBucket | null | undefined): string => {
  const parts: string[] = [];

  if (bucket?.confirmedCount != null) {
    parts.push(`${bucket.confirmedCount} confirmed`);
  }

  if (bucket?.probableCount != null) {
    parts.push(`${bucket.probableCount} probable`);
  }

  return parts.length > 0 ? ` (${parts.join(' · ')})` : '';
};

const WarningStat = ({
  label,
  bucket,
}: {
  label: string;
  bucket: EndsFdaWarningBucket | null | undefined;
}) => {
  const count = bucket?.count ?? 0;
  const isActive = count > 0;

  return (
    <div>
      <div className="text-sm font-semibold text-slate-900">{label}</div>
      <div
        className={ctw('mt-1 text-sm', isActive ? 'font-semibold text-red-600' : 'text-slate-500')}
      >
        {count} active warning{count === 1 ? '' : 's'}
        {isActive ? confidenceSuffix(bucket) : ''}
      </div>
    </div>
  );
};

export const FdaWarningsSection = ({ fdaWarnings }: FdaWarningsSectionProps) => {
  if (!fdaWarnings) {
    return null;
  }

  const {
    againstMerchant,
    againstProductSold,
    findingsNarrative,
    findingsNarrativeSecondary,
    freshnessNote,
    referenceLinks,
    inspectionHistory,
  } = fdaWarnings;

  const bucketNotes = [againstMerchant?.note, againstProductSold?.note].filter(
    (note): note is string => note != null && note.trim() !== '',
  );
  const letters = [...(againstProductSold?.letters ?? []), ...(againstMerchant?.letters ?? [])];

  return (
    <ReportWrapper
      title={
        <span className="inline-flex items-center gap-2">
          <FdaGlyph />
          FDA Warnings
        </span>
      }
      showTooltip={false}
    >
      <div className="flex flex-wrap gap-16">
        <WarningStat label="Against merchant" bucket={againstMerchant} />
        <WarningStat label="Against product sold" bucket={againstProductSold} />
      </div>

      {(findingsNarrative || findingsNarrativeSecondary || bucketNotes.length > 0) && (
        <div className="mt-6">
          <h4 className="mb-2 text-sm font-semibold text-slate-900">Findings</h4>
          {findingsNarrative && (
            <p className="text-sm leading-relaxed text-slate-700">{findingsNarrative}</p>
          )}
          {findingsNarrativeSecondary && (
            <p className="mt-3 text-sm italic leading-relaxed text-slate-500">
              {findingsNarrativeSecondary}
            </p>
          )}
          {bucketNotes.map((note, index) => (
            <p key={index} className="mt-3 text-sm leading-relaxed text-slate-700">
              {note}
            </p>
          ))}
        </div>
      )}

      {freshnessNote && (
        <div className="mt-6 rounded-2xl border border-gray-200 bg-gray-50 px-5 py-4 text-sm leading-relaxed text-slate-600">
          <span className="font-semibold text-slate-900">Freshness note:</span> {freshnessNote}
        </div>
      )}

      {letters.length > 0 && <FdaLettersTable letters={letters} />}

      {referenceLinks && referenceLinks.length > 0 && (
        <ul className="mt-6 space-y-2">
          {referenceLinks.map((link, index) => {
            const isLink = link.url != null && isAbsoluteHttpUrl(link.url);
            const text = link.label ?? link.url;

            if (text == null) {
              return null;
            }

            return (
              <li key={index} className="flex items-center gap-2 text-sm">
                <FileText className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
                {isLink ? (
                  <a
                    href={link.url ?? undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-blue-600 underline underline-offset-2 hover:text-blue-800"
                  >
                    {text}
                  </a>
                ) : (
                  <span className="text-slate-500">{text}</span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {inspectionHistory && <FdaInspectionHistory inspectionHistory={inspectionHistory} />}
    </ReportWrapper>
  );
};
